import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../services/api';
import { useTheme } from '../App';
import { useUser } from '../context/UserContext';

const IdeaOfTheDay = () => {
  const { t, i18n } = useTranslation();
  const { user } = useUser();
  const navigate = useNavigate();
  const [idea, setIdea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch today's idea (get first idea ID, then fetch full details)
  useEffect(() => {
    const fetchTodaysIdea = async () => {
      try {
        setLoading(true);
        
        // First get the list to find the first idea ID
        const listResponse = await apiClient.getIdeas({}, 1, 1, i18n.language);
        const ideas = listResponse.ideas || listResponse.data || listResponse;
        
        if (ideas && ideas.length > 0) {
          const firstIdeaId = ideas[0].id;
          
          // Then fetch the full details including competitors using getIdeaById
          const detailResponse = await apiClient.getIdeaById(firstIdeaId, i18n.language);
          const ideaData = detailResponse.idea || detailResponse.data || detailResponse;
          
          // Transform Supabase response format to frontend format
          if (ideaData) {
            // Transform competitors from array or null to array
            ideaData.competitors = ideaData.idea_competitors || ideaData.competitors || [];
            
            // Transform financial data from array to object
            ideaData.financialData = ideaData.idea_financial_data?.[0] || ideaData.financialData || null;
            
            // Transform technical requirements from array to object  
            ideaData.technicalRequirements = ideaData.idea_technical_requirements?.[0] || ideaData.technicalRequirements || null;
          }
          
          setIdea(ideaData);
        }
      } catch (err) {
        setError(err.message);
        console.error('Failed to fetch today\'s idea:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTodaysIdea();
  }, [i18n.language]); // Re-fetch when language changes
  const { darkMode } = useTheme();
  const isRTL = i18n.language === 'ar';

  // Helper function to get color based on value and type
  const getValueColor = (value, type) => {
    if (!value) return darkMode ? 'text-gray-300' : 'text-gray-600';
    const lowerValue = value.toLowerCase();
    
    switch (type) {
      case 'competition':
        if (lowerValue.includes('high')) return darkMode ? 'text-red-400' : 'text-red-600';
        if (lowerValue.includes('medium')) return darkMode ? 'text-yellow-400' : 'text-yellow-600';
        if (lowerValue.includes('low')) return darkMode ? 'text-green-400' : 'text-green-600';
        return darkMode ? 'text-gray-300' : 'text-gray-600';
      
      case 'risk':
        if (lowerValue.includes('high')) return darkMode ? 'text-red-400' : 'text-red-600';
        if (lowerValue.includes('medium')) return darkMode ? 'text-yellow-400' : 'text-yellow-600';
        if (lowerValue.includes('low')) return darkMode ? 'text-green-400' : 'text-green-600';
        return darkMode ? 'text-gray-300' : 'text-gray-600';
      
      case 'timing':
        if (lowerValue.includes('perfect') || lowerValue.includes('excellent')) return darkMode ? 'text-green-400' : 'text-green-600';
        if (lowerValue.includes('good')) return darkMode ? 'text-blue-400' : 'text-blue-600';
        if (lowerValue.includes('poor') || lowerValue.includes('bad')) return darkMode ? 'text-red-400' : 'text-red-600';
        return darkMode ? 'text-purple-400' : 'text-purple-600';
      
      case 'revenue':
        if (lowerValue.includes('subscription')) return darkMode ? 'text-blue-400' : 'text-blue-600';
        if (lowerValue.includes('one-time')) return darkMode ? 'text-green-400' : 'text-green-600';
        if (lowerValue.includes('freemium')) return darkMode ? 'text-purple-400' : 'text-purple-600';
        if (lowerValue.includes('advertising')) return darkMode ? 'text-orange-400' : 'text-orange-600';
        return darkMode ? 'text-gray-300' : 'text-gray-600';
      
      default:
        return darkMode ? 'text-gray-300' : 'text-gray-600';
    }
  };

  // Helper function to get icon based on type
  const getIcon = (type) => {
    switch (type) {
      case 'competition':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
      case 'risk':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      case 'timing':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'revenue':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
          </svg>
        );
      default:
        return null;
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="fade-in-soft">
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className={darkMode ? 'text-gray-300' : 'text-slate-600'}>Loading today's idea...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="fade-in-soft">
        <div className={`p-6 rounded-2xl shadow-soft-lg text-center ${
          darkMode ? 'bg-red-900 bg-opacity-20 border border-red-500' : 'bg-red-50 border border-red-200'
        }`}>
          <p className={`mb-4 ${
            darkMode ? 'text-red-300' : 'text-red-600'
          }`}>
            Error loading today's idea: {error}
          </p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-soft btn-soft-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // No idea found state
  if (!idea) {
    return (
      <div className="fade-in-soft">
        <div className="text-center py-16">
          <p className={darkMode ? 'text-gray-300' : 'text-slate-600'}>No idea available today.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in-soft">
      {/* Hero Section */}
      <div className="mb-8">
        <h1 className={`text-4xl md:text-5xl font-bold mb-4 ${
          darkMode ? 'text-white' : 'text-slate-700'
        } text-start`}>
          {t('navigation.ideaOfTheDay')}
        </h1>
        <p className={`text-lg mb-6 ${
          darkMode ? 'text-gray-300' : 'text-slate-500'
        } text-start`}>
          {t('ideaOfTheDay.description')}
        </p>
        <div className={`w-24 h-1 bg-gradient-fuchsia rounded-full ${isRTL ? 'ml-0 mr-auto' : 'ml-0 mr-auto'}`}></div>
      </div>

      {/* Main Idea Content */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-medium">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            {t(`filterOptions.type.${idea.type}`) || idea.type}
          </div>
          <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-green-100 text-green-800 text-xs font-medium">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            {t(`filterOptions.industry.${idea.industry}`) || idea.industry}
          </div>
          <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-purple-100 text-purple-800 text-xs font-medium">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            {t(`filterOptions.executionDifficulty.${idea.execution_difficulty}`) || idea.execution_difficulty}
          </div>
          <div className="text-sm text-slate-500 ml-auto">
            {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>
        
        <h2 className={`text-3xl font-bold mb-4 ${
          darkMode ? 'text-white' : 'text-slate-700'
        }`}>{idea.title}</h2>
        <p className={`text-lg leading-relaxed mb-8 ${
          darkMode ? 'text-gray-300' : 'text-slate-600'
        }`}>{idea.description}</p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className={`rounded-2xl shadow-soft-lg p-4 text-center ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className={`text-xs font-semibold uppercase tracking-wide mb-2 flex items-center justify-center gap-1 ${
              darkMode ? 'text-gray-300' : 'text-slate-500'
            }`}>
              <span className={getValueColor(idea.competition, 'competition')}>
                {getIcon('competition')}
              </span>
              {t('ideasVault.competition')}
            </div>
            <div className={`text-sm font-semibold ${
              getValueColor(idea.competition, 'competition')
            }`}>{t(`filterOptions.competition.${idea.competition}`) || idea.competition}</div>
          </div>
          <div className={`rounded-2xl shadow-soft-lg p-4 text-center ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className={`text-xs font-semibold uppercase tracking-wide mb-2 flex items-center justify-center gap-1 ${
              darkMode ? 'text-gray-300' : 'text-slate-500'
            }`}>
              <span className={getValueColor(idea.regulatory_risk, 'risk')}>
                {getIcon('risk')}
              </span>
              {t('ideasVault.risk')}
            </div>
            <div className={`text-sm font-semibold ${
              getValueColor(idea.regulatory_risk, 'risk')
            }`}>{t(`filterOptions.regulatoryRisk.${idea.regulatory_risk}`) || idea.regulatory_risk}</div>
          </div>
          <div className={`rounded-2xl shadow-soft-lg p-4 text-center ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className={`text-xs font-semibold uppercase tracking-wide mb-2 flex items-center justify-center gap-1 ${
              darkMode ? 'text-gray-300' : 'text-slate-500'
            }`}>
              <span className={getValueColor(idea.timing, 'timing')}>
                {getIcon('timing')}
              </span>
              {t('ideasVault.timing')}
            </div>
            <div className={`text-sm font-semibold ${
              getValueColor(idea.timing, 'timing')
            }`}>{t(`filterOptions.timing.${idea.timing}`) || idea.timing}</div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to={`/idea/${idea.id}`} className="btn-soft btn-soft-primary flex-1 sm:flex-initial text-center">
            {t('ideaOfTheDay.viewFullAnalysis')}
          </Link>
          <button 
            onClick={async () => {
              console.log('Build button clicked', { user: !!user, ideaId: idea?.id });
              
              // Record build click
              if (user && idea?.id) {
                try {
                  const token = localStorage.getItem('token');
                  if (token) {
                    console.log('Recording build click for idea:', idea.id);
                    const response = await apiClient.recordBuildClick(idea.id, token);
                    console.log('Build click recorded successfully:', response);
                  } else {
                    console.warn('No token found for build click recording');
                  }
                } catch (error) {
                  console.error('Failed to record build click:', error);
                }
              } else {
                console.warn('Cannot record build click - missing user or idea');
              }
              
              // Navigate to builders studio
              navigate(`/builders-studio?title=${encodeURIComponent(idea.title)}&description=${encodeURIComponent(idea.description)}`);
            }}
            className="btn-soft btn-soft-secondary flex-1 sm:flex-initial text-center"
          >
            {t('ideaOfTheDay.buildThisIdea')}
          </button>
        </div>
      </div>

      {/* Quick Overview */}
      <div className="mb-8">
        <h3 className={`text-2xl font-bold mb-6 ${
          darkMode ? 'text-white' : 'text-slate-700'
        } text-start`}>
          {t('ideaOfTheDay.quickOverview')}
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Why Now Box */}
          <div className={`group relative flex h-full cursor-pointer flex-col justify-between overflow-hidden rounded-xl p-6 transition-all duration-500 hover:scale-[1.02] ${
            darkMode 
              ? 'bg-gradient-to-br from-indigo-900/30 via-purple-800/20 to-pink-900/30 border border-indigo-500/20 hover:border-indigo-400/40' 
              : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 border border-indigo-200/50 hover:border-indigo-300/60'
          }`}>
            {/* Grid Pattern Background */}
            <div className={`absolute -right-1/2 top-0 z-0 size-full ${
              darkMode 
                ? 'bg-[linear-gradient(to_right,#4f46e530_1px,transparent_1px),linear-gradient(to_bottom,#4f46e530_1px,transparent_1px)]' 
                : 'bg-[linear-gradient(to_right,#4f46e520_1px,transparent_1px),linear-gradient(to_bottom,#4f46e520_1px,transparent_1px)]'
            } bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]`}></div>
            
            {/* Large Background Icon */}
            <div className={`absolute bottom-3 right-1 scale-[6] transition-all duration-700 group-hover:scale-[6.2] ${
              darkMode ? 'text-indigo-500/5 group-hover:text-indigo-400/10' : 'text-indigo-500/5 group-hover:text-indigo-500/10'
            }`}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            
            <div className="relative z-10 flex h-full flex-col justify-between">
              <div>
                <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-full shadow transition-all duration-500 ${
                  darkMode 
                    ? 'bg-indigo-500/20 text-indigo-300 shadow-indigo-500/20 group-hover:bg-indigo-500/30 group-hover:shadow-indigo-400/30' 
                    : 'bg-indigo-100 text-indigo-600 shadow-indigo-500/20 group-hover:bg-indigo-200 group-hover:shadow-indigo-500/30'
                }`}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h4 className={`mb-2 text-xl font-semibold tracking-tight ${
                  darkMode ? 'text-white' : 'text-slate-700'
                } text-start`}>{t('ideaOfTheDay.whyNow')}</h4>
                <p className={`text-sm leading-relaxed ${
                  darkMode ? 'text-indigo-100' : 'text-indigo-700'
                }`}>{idea.why_now}</p>
              </div>
            </div>
            <div className={`absolute bottom-0 left-0 h-1 w-full blur-2xl transition-all duration-500 group-hover:blur-lg ${
              darkMode ? 'bg-gradient-to-r from-indigo-400 to-purple-400' : 'bg-gradient-to-r from-indigo-500 to-purple-500'
            }`} />
          </div>

          {/* Revenue Model Box */}
          <div className={`group relative flex h-full cursor-pointer flex-col justify-between overflow-hidden rounded-xl p-6 transition-all duration-500 hover:scale-[1.02] ${
            darkMode 
              ? 'bg-gradient-to-br from-teal-900/30 via-cyan-800/20 to-blue-900/30 border border-teal-500/20 hover:border-teal-400/40' 
              : 'bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 border border-teal-200/50 hover:border-teal-300/60'
          }`}>
            {/* Grid Pattern Background */}
            <div className={`absolute -right-1/2 top-0 z-0 size-full ${
              darkMode 
                ? 'bg-[linear-gradient(to_right,#14b8a630_1px,transparent_1px),linear-gradient(to_bottom,#14b8a630_1px,transparent_1px)]' 
                : 'bg-[linear-gradient(to_right,#14b8a620_1px,transparent_1px),linear-gradient(to_bottom,#14b8a620_1px,transparent_1px)]'
            } bg-[size:24px_24px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]`}></div>
            
            {/* Large Background Icon */}
            <div className={`absolute bottom-3 right-1 scale-[6] transition-all duration-700 group-hover:scale-[6.2] ${
              darkMode ? 'text-teal-500/5 group-hover:text-teal-400/10' : 'text-teal-500/5 group-hover:text-teal-500/10'
            }`}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            
            <div className="relative z-10 flex h-full flex-col justify-between">
              <div>
                <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-full shadow transition-all duration-500 ${
                  darkMode 
                    ? 'bg-teal-500/20 text-teal-300 shadow-teal-500/20 group-hover:bg-teal-500/30 group-hover:shadow-teal-400/30' 
                    : 'bg-teal-100 text-teal-600 shadow-teal-500/20 group-hover:bg-teal-200 group-hover:shadow-teal-500/30'
                }`}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <h4 className={`mb-2 text-xl font-semibold tracking-tight ${
                  darkMode ? 'text-white' : 'text-slate-700'
                } text-start`}>{t('ideaOfTheDay.revenueModel')}</h4>
                <p className={`text-sm leading-relaxed ${
                  darkMode ? 'text-teal-100' : 'text-teal-700'
                }`}>{idea.revenue_model}</p>
              </div>
            </div>
            <div className={`absolute bottom-0 left-0 h-1 w-full blur-2xl transition-all duration-500 group-hover:blur-lg ${
              darkMode ? 'bg-gradient-to-r from-teal-400 to-cyan-400' : 'bg-gradient-to-r from-teal-500 to-cyan-500'
            }`} />
          </div>
        </div>
      </div>

      {/* Competitors Section */}
      <div className="mb-8">
        <h3 className={`text-2xl font-bold mb-6 ${
          darkMode ? 'text-white' : 'text-slate-700'
        } text-start`}>
          {t('ideaOfTheDay.topCompetitors')}
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          {idea.competitors && idea.competitors.length > 0 ? idea.competitors.map((competitor, index) => (
            <div key={index} className={`p-6 hover-soft rounded-2xl shadow-soft-lg ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <h4 className={`text-xl font-semibold mb-4 ${
                darkMode ? 'text-white' : 'text-slate-700'
              }`}>{competitor.name}</h4>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center mb-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 ${
                      darkMode ? 'bg-white bg-opacity-20' : 'bg-green-100'
                    }`}>
                      <span className={`text-sm ${
                        darkMode ? 'text-white' : 'text-green-600'
                      }`}>✓</span>
                    </div>
                    <span className={`font-semibold ${
                      darkMode ? 'text-white' : 'text-slate-700'
                    }`}>{t('ideaOfTheDay.strengths')}</span>
                  </div>
                  <ul className="space-y-1 ml-8">
                    {competitor.strengths && competitor.strengths.length > 0 ? competitor.strengths.map((strength, i) => (
                      <li key={i} className={`text-sm flex items-start ${
                        darkMode ? 'text-gray-300' : 'text-slate-600'
                      }`}>
                        <span className={`mr-2 mt-0.5 ${
                          darkMode ? 'text-gray-300' : 'text-green-500'
                        }`}>•</span>
                        {strength}
                      </li>
                    )) : (
                      <li className={`text-sm ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>No strengths listed</li>
                    )}
                  </ul>
                </div>
                <div>
                  <div className="flex items-center mb-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-2 ${
                      darkMode ? 'bg-white bg-opacity-20' : 'bg-orange-100'
                    }`}>
                      <span className={`text-sm ${
                        darkMode ? 'text-white' : 'text-orange-600'
                      }`}>!</span>
                    </div>
                    <span className={`font-semibold ${
                      darkMode ? 'text-white' : 'text-slate-700'
                    }`}>{t('ideaOfTheDay.weaknesses')}</span>
                  </div>
                  <ul className="space-y-1 ml-8">
                    {competitor.weaknesses && competitor.weaknesses.length > 0 ? competitor.weaknesses.map((weakness, i) => (
                      <li key={i} className={`text-sm flex items-start ${
                        darkMode ? 'text-gray-300' : 'text-slate-600'
                      }`}>
                        <span className={`mr-2 mt-0.5 ${
                          darkMode ? 'text-gray-300' : 'text-orange-500'
                        }`}>•</span>
                        {weakness}
                      </li>
                    )) : (
                      <li className={`text-sm ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>No weaknesses listed</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )) : (
            <div className={`col-span-2 text-center py-8 ${darkMode ? 'text-gray-400' : 'text-slate-500'}`}>
              No competitors data available
            </div>
          )}
        </div>
      </div>

      {/* Target Audience */}
      <div className={`p-6 mb-8 hover-soft rounded-2xl shadow-soft-lg ${
        darkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        <h4 className={`text-xl font-semibold mb-4 ${
          darkMode ? 'text-white' : 'text-slate-700'
        } text-start`}>{t('ideaOfTheDay.targetAudience')}</h4>
        <p className={`leading-relaxed ${
          darkMode ? 'text-gray-300' : 'text-slate-600'
        }`}>{idea.target_audience}</p>
      </div>

      {/* Call to Action */}
      <div>
        <div className={`p-8 rounded-2xl shadow-soft-lg ${
          darkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          <h3 className={`text-2xl font-bold mb-4 ${
            darkMode ? 'text-white' : 'text-slate-700'
          } text-start`}>{t('ideaOfTheDay.readyToExplore')}</h3>
          <p className={`mb-6 ${
            darkMode ? 'text-gray-300' : 'text-slate-600'
          } text-start`}>
            {t('ideaOfTheDay.diveDeeper')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/ideas-vault" className="btn-soft btn-soft-secondary">
              {t('ideaOfTheDay.exploreMoreIdeas')}
            </Link>
            <Link to={`/builders-studio?title=${encodeURIComponent(idea.title)}&description=${encodeURIComponent(idea.description)}`} className="btn-soft btn-soft-primary">
              {t('ideaOfTheDay.startBuilding')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IdeaOfTheDay;